import { memo, ReactElement, useState } from 'react'; 
import { Box } from '@mui/material';
import { CAB_PANEL_WIDTH } from '@CabComponents/CabPanel';
import {
  MeetingSlot, MeetingHoldEventErrorResolution, Meeting, Calendar, InvalidParticipantVotes, 
  MeetingHoldEventError, RecurrenceRule,
} from '../../store';
import { UICalendarEventConsolidated } from '../../store/schedule/types';
import MeetingSettingsContainer from '../../components/Schedule/MeetingSettings';
import CabinetPage from '../../components/Common/CabinetPage';
import { CabinetModal } from '../../components/Common/CabinetModal';
import MeetingErrors from '../../components/Schedule/MeetingErrors';
import Header from './Header';
import CalendarSchedulerContainer from '../../components/Schedule/CalendarScheduler';
import ScheduleShareModalContainer from '../../components/Schedule/ScheduleShareModal';
import colors from '../../colors';
import DuplicateMeetingModal, {
  DuplicateMeetingSubmitProps
} from '../../components/Schedule/DuplicateMeetingModal/DuplicateMeetingModal';
import { useConfirmationModalWithUI } from '../../components/Common/ConfirmationModal';

import ReusableMeetingModal from '../../components/Schedule/ReusableMeetingModal';
import CabSpinner from '@CabComponents/CabSpinner';
import { ExcludedSlotInfo } from '../../utils/scheduleUtils';
import { SlotInfo } from 'react-big-calendar';
import SidePanel from './SidePanel';


const calendarSchedulerSx = { padding: 0, paddingTop: 0 };


interface ScheduleProps {
  currentMeeting: Meeting | null;
  currentMeetingErrors: MeetingHoldEventError[];
  errorModalOpen: boolean;
  handleResolution: (resolution: MeetingHoldEventErrorResolution) => void;
  calendars: Calendar[];
  handleCancel: () => void;
  handleOpenMeeting: (meetingId: number) => void;
  allLoaded: boolean;
  openMeetingSettings: boolean;
  selectedLeaders: number[];
  selectedSlots: MeetingSlot[];
  recurringSlots: MeetingSlot[];
  handleCreateSlot: (info: SlotInfo) => void;
  handleEditSlot: (eventId: string, start: Date, end: Date, isExcluded: boolean) => void;
  handleDeleteSlots: (slots: MeetingSlot[]) => void;
  handleDuplicateMeeting: (meetingId: number) => void;
  handleDeleteMeeting: (meetingId: number) => void;
  handleShareMeeting: (meetingId: number) => void;
  handleCopyLink: (meetingId: number) => void;
  handleConvertToOneOff: (meetingId: number) => void;
  currentMeetingSlots: MeetingSlot[];
  updateSlotNames: (name: string) => void;
  setErrorModalOpen: (open: boolean) => void;
  handleShareCurrentMeeting: () => void;
  updatingSlots: boolean;
  pollUpdateOpen: boolean;
  handlePollUpdateAccept: () => void;
  handlePollUpdateReject: () => void;
  invalidatedParticipantVotes: InvalidParticipantVotes;
  handleCreateExcludeSlots: (slots: ExcludedSlotInfo[]) => void;
  handleEditRecurrence: (rule: RecurrenceRule, index: number) => void;
  handleDeleteRecurrence: (index: number) => void;
  handleOpenRecurrenceIndex: (index: number) => void;
  handleEditRecurrenceTime: (index: number, start: Date, end: Date) => void;
  shareMeetingModalMeetingId: number;
  setShareMeetingModalMeetingId: (id: number) => void;
  setDuplicateMeetingModalMeetingId: (id: number) => void;
  handleSubmitDuplicateMeeting: (data: DuplicateMeetingSubmitProps) => Promise<Meeting | undefined>;
  meetingToDuplicate?: Meeting;
  
  // Event fetching parameters
  googleCalIds: string[];
  msCalIds: string[];
  forTimezone: string;
  showCanceledDeclined?: boolean;
  prefetchNextPeriod?: boolean;
  
  // Event data callbacks
  onEventsLoaded?: (events: UICalendarEventConsolidated[]) => void;
  onLoadingChange?: (loading: boolean) => void;
  onError?: (error: unknown) => void;
}

const Schedule = ({
  currentMeeting, currentMeetingErrors, errorModalOpen, handleResolution, calendars,
  handleCancel, handleOpenMeeting, allLoaded, openMeetingSettings, selectedLeaders,
  selectedSlots, recurringSlots, handleCreateSlot, handleEditSlot, handleDeleteSlots, handleDuplicateMeeting,
  handleDeleteMeeting, handleShareMeeting, handleCopyLink, handleConvertToOneOff, currentMeetingSlots, updateSlotNames,
  setErrorModalOpen, handleShareCurrentMeeting, updatingSlots, pollUpdateOpen,
  handlePollUpdateAccept, handlePollUpdateReject, invalidatedParticipantVotes, handleCreateExcludeSlots,
  handleEditRecurrence, handleDeleteRecurrence, handleOpenRecurrenceIndex, handleEditRecurrenceTime,
  shareMeetingModalMeetingId, setShareMeetingModalMeetingId, setDuplicateMeetingModalMeetingId,
  handleSubmitDuplicateMeeting, meetingToDuplicate,
  googleCalIds, msCalIds, forTimezone, showCanceledDeclined = true, prefetchNextPeriod = false,
  onEventsLoaded, onLoadingChange, onError,
}: ScheduleProps): ReactElement => {
  const [openSidePanel, setOpenSidePanel] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(false);

  const { confirm: confirmDelete, ConfirmationModalComponent: DeleteConfirmationModal } = useConfirmationModalWithUI();

  const handleToggleSidePanel = () => {
    setOpenSidePanel(!openSidePanel);
  };

  const handleDeleteMeetingWithConfirmation = async (meetingId: number) => {
    const confirmed = await confirmDelete({
      title: "Confirm Deletion",
      message: 'Are you sure you want to delete this meeting record?',
      confirmText: "Delete", cancelText: "Cancel", isDestructive: true,
    });
    
    if (confirmed) {
      handleDeleteMeeting(meetingId);
    }
  };

  return (
    <CabinetPage
      pageName={'Schedule'}
      // headerBackgroundColor={colors.calmGrey50}
      headerBackgroundColor={colors.white900}
      headerContent={<>
        <CabinetModal
          open={currentMeetingErrors.length > 0 ? errorModalOpen : false}
          component={
            <MeetingErrors
              meeting={currentMeeting}
              meetingErrors={currentMeetingErrors}
              handleResolution={handleResolution}
              calendars={calendars}
            />
          }
          onClose={() => setErrorModalOpen(false)}         
        />
        <Box
          sx={{
            width: "100%",
            marginRight: openMeetingSettings ? `${CAB_PANEL_WIDTH}px` : 0
          }}>
          <Header onToggleSidePanel={handleToggleSidePanel} />
        </Box>
      </>}
    >
      {((loadingEvents) || !allLoaded) && (
        <Box sx={{
          position: 'absolute',
          zIndex: 200,
          height: '100%',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <CabSpinner scale={4} />
        </Box>
      ) }
      <Box
        sx={{
          display: "flex",
          flex: 1,
          height: "100%"
        }}>
        <Box
          sx={{
            display: "flex",
            flex: 1,
            flexDirection: "row",
            gap: 1,
            marginRight: openMeetingSettings ? 0 : `-${CAB_PANEL_WIDTH}px`
          }}>
          <SidePanel
            open={openSidePanel}
            onCancel={() => setOpenSidePanel(false)}
          />

          <CalendarSchedulerContainer
            selectedLeaders={selectedLeaders}
            handleOpenMeeting={handleOpenMeeting}
            selectedSlots={selectedSlots}
            recurringSlots={recurringSlots}
            handleOpenRecurrenceIndex={handleOpenRecurrenceIndex}
            handleDeleteRecurrenceIndex={handleDeleteRecurrence}
            handleEditRecurrenceIndex={handleEditRecurrenceTime}
            handleSlotsCreated={handleCreateSlot}
            handleEditSlot={handleEditSlot}
            handleDeleteSlots={handleDeleteSlots}
            handleDuplicateMeeting={handleDuplicateMeeting}
            handleDeleteMeeting={handleDeleteMeetingWithConfirmation}
            handleShareMeeting={handleShareMeeting}
            handleCopyLink={handleCopyLink}
            hideCalendarHeader={true}
            sx={calendarSchedulerSx}
            googleCalIds={googleCalIds}
            msCalIds={msCalIds}
            forTimezone={forTimezone}
            showCanceledDeclined={showCanceledDeclined}
            prefetchNextPeriod={prefetchNextPeriod}
            onEventsLoaded={onEventsLoaded}
            onLoadingChange={setLoadingEvents}
            onError={onError}
          />
        </Box>
        {currentMeeting?.use_template_parent && currentMeeting.template_parent ? (
          <ReusableMeetingModal
            open={!!currentMeeting}
            meeting={currentMeeting}
            onClose={handleCancel}
            onConvertToOneOff={() => handleConvertToOneOff(currentMeeting.id)}
            onEditReusable={() => currentMeeting.template_parent && handleOpenMeeting(currentMeeting.template_parent)}
          />
        ) : (
          <MeetingSettingsContainer
            meetingSlots={currentMeetingSlots}
            onUpdateMeetingName={updateSlotNames}
            onEditSlot={handleEditSlot}
            onDeleteSlots={handleDeleteSlots}
            onShowErrors={setErrorModalOpen}
            meetingErrors={currentMeetingErrors}
            onShare={handleShareCurrentMeeting}
            onCancel={handleCancel}
            openMeetingSettings={openMeetingSettings}
            slotsAreRecalculating={updatingSlots}
            pollUpdateOpen={pollUpdateOpen}
            onPollUpdateAccept={handlePollUpdateAccept}
            onPollUpdateReject={handlePollUpdateReject}
            invalidMeetingSlots={invalidatedParticipantVotes}
            onExcludedSlotsCreated={handleCreateExcludeSlots}
            onEditRecurrence={handleEditRecurrence}
            onDeleteRecurrence={handleDeleteRecurrence}
          />
        )}
        <ScheduleShareModalContainer
          key={shareMeetingModalMeetingId}
          meetingId={shareMeetingModalMeetingId}
          onModalClose={() => setShareMeetingModalMeetingId(-1)}
          modalOpen={shareMeetingModalMeetingId > 0}
          showEdit
        />
        <DuplicateMeetingModal
          open={!!meetingToDuplicate}
          onClose={() => setDuplicateMeetingModalMeetingId(-1)}
          meeting={meetingToDuplicate}
          submitDuplicate={handleSubmitDuplicateMeeting}
        />
        <DeleteConfirmationModal />
      </Box>
    </CabinetPage>
  );
};

export default memo(Schedule);
